/**
 * Speakeasy quick-view modal + one-tap card add-to-bag.
 * Markup shell: snippets/speakeasy-quick-view.liquid (#Speakeasy-QuickView).
 * Styles: assets/speakeasy-quick-view.css.
 *
 * Delegated document click targets (buttons rendered by the product card snippet):
 *   .spk-quick-view-trigger[data-product-url] -> open modal, fetch {url}.js, render
 *   .spk-card-add[data-variant-id]            -> POST /cart/add.js directly
 *
 * After a successful add, dispatches document CustomEvents
 * 'speakeasy:cart-drawer:refresh' then 'speakeasy:cart-drawer:open', and shows a
 * toast via window.Speakeasy.toast (fallback: 'speakeasy:toast' CustomEvent).
 *
 * Reuses Dawn's global focus helpers (window.trapFocus / window.removeTrapFocus)
 * and the body 'overflow-hidden' scroll-lock class. Never throws uncaught: every
 * fetch path resolves to either rendered content, the built-in error state, or a
 * toast message.
 */

(function () {
  'use strict';

  if (window.SpeakeasyQuickViewInit) return;
  window.SpeakeasyQuickViewInit = true;

  var TRIGGER_SELECTOR = '.spk-quick-view-trigger[data-product-url]';
  var CARD_ADD_SELECTOR = '.spk-card-add[data-variant-id]';
  var IMAGE_WIDTHS = [360, 540, 720, 900, 1080];

  var root = document.getElementById('Speakeasy-QuickView');
  var dialog = root ? root.querySelector('.spk-qv__dialog') : null;
  var els = root
    ? {
        skeleton: root.querySelector('.spk-qv__skeleton'),
        error: root.querySelector('.spk-qv__error'),
        retry: root.querySelector('.spk-qv__retry'),
        content: root.querySelector('.spk-qv__content'),
        img: root.querySelector('.spk-qv__img'),
        maker: root.querySelector('.spk-qv__maker'),
        title: root.querySelector('.spk-qv__title'),
        price: root.querySelector('.spk-qv__price'),
        priceCurrent: root.querySelector('.spk-qv__price-current'),
        priceCompare: root.querySelector('.spk-qv__price-compare'),
        priceCompareValue: root.querySelector('.spk-qv__price-compare-value'),
        options: root.querySelector('.spk-qv__options'),
        desc: root.querySelector('.spk-qv__desc'),
        add: root.querySelector('.spk-qv__add'),
        details: root.querySelector('.spk-qv__details')
      }
    : null;

  var openedBy = null;
  var state = {
    product: null,
    options: [], // [{ name, values: [...] }]
    selected: [], // chosen value per option index
    variant: null,
    detailsUrl: '',
    lastUrl: '',
    requestToken: 0,
    adding: false
  };

  var strings = {
    add: 'Add to bag',
    adding: 'Adding…',
    soldOut: (window.variantStrings && window.variantStrings.soldOut) || 'Sold out',
    unavailable: (window.variantStrings && window.variantStrings.unavailable) || 'Unavailable',
    addError: (window.cartStrings && window.cartStrings.error) || 'Couldn’t add this to your bag.'
  };

  /* ---------------------------------- utilities ---------------------------------- */

  function toast(message) {
    if (!message) return;
    try {
      if (window.Speakeasy && typeof window.Speakeasy.toast === 'function') {
        window.Speakeasy.toast(message);
        return;
      }
      document.dispatchEvent(new CustomEvent('speakeasy:toast', { detail: { message: message } }));
    } catch (error) {
      /* toast is best-effort only */
    }
  }

  function formatMoney(cents) {
    var amount = Number(cents) / 100;
    if (!isFinite(amount)) amount = 0;
    var currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(
        amount
      );
    } catch (error) {
      return currency + ' ' + amount.toFixed(2);
    }
  }

  /* data-product-url is a clean path, but survive query strings / an existing .js suffix. */
  function productJsonUrl(url) {
    if (!url) return '';
    var path = String(url).split('#')[0];
    var query = '';
    var queryIndex = path.indexOf('?');
    if (queryIndex > -1) {
      query = path.slice(queryIndex + 1);
      path = path.slice(0, queryIndex);
    }
    if (!/\.js$/.test(path)) path += '.js';
    return query ? path + '?' + query : path;
  }

  function imageSrcOf(image) {
    if (!image) return '';
    if (typeof image === 'string') return image;
    return typeof image.src === 'string' ? image.src : '';
  }

  function imageWidthUrl(src, width) {
    var url = String(src);
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* Strip description HTML to plain text and truncate around maxLength chars. */
  function stripHtml(html, maxLength) {
    if (!html) return '';
    var text = '';
    try {
      text = new DOMParser().parseFromString(String(html), 'text/html').body.textContent || '';
    } catch (error) {
      var scratch = document.createElement('div');
      scratch.innerHTML = String(html).replace(/<script[\s\S]*?<\/script>/gi, '');
      text = scratch.textContent || '';
    }
    text = text.replace(/\s+/g, ' ').trim();
    if (maxLength && text.length > maxLength) {
      var cut = text.slice(0, maxLength + 1);
      var lastSpace = cut.lastIndexOf(' ');
      if (lastSpace > maxLength * 0.6) cut = cut.slice(0, lastSpace);
      text = cut.replace(/[\s.,;:!\-–—]+$/, '') + '…';
    }
    return text;
  }

  /* Product AJAX JSON variants carry both options[] and option1/2/3. */
  function variantOptionValue(variant, index) {
    if (variant.options && variant.options.length) return variant.options[index];
    return variant['option' + (index + 1)];
  }

  /* product.options may be strings or {name, values} objects depending on endpoint shape. */
  function normalizeOptions(product) {
    var raw = product.options || [];
    return raw.map(function (option, index) {
      var name =
        typeof option === 'string' ? option : (option && option.name) || 'Option ' + (index + 1);
      var values =
        option && option.values && typeof option.values.slice === 'function'
          ? option.values.slice()
          : [];
      if (!values.length) {
        product.variants.forEach(function (variant) {
          var value = variantOptionValue(variant, index);
          if (value !== null && value !== undefined && values.indexOf(value) === -1) {
            values.push(value);
          }
        });
      }
      return { name: name, values: values };
    });
  }

  /* ------------------------------------ cart ------------------------------------- */

  function postAdd(variantId) {
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    if (!/\.js$/.test(addUrl)) addUrl += '.js';
    return fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    }).then(function (response) {
      return response
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!response.ok || (data && data.status && data.status >= 400)) {
            var error = new Error('Cart add failed');
            error.description = (data && (data.description || data.message)) || '';
            throw error;
          }
          return data;
        });
    });
  }

  function addedTitle(payload, fallback) {
    var item = payload && payload.items && payload.items[0];
    if (!item && payload && payload.product_title) item = payload;
    return (item && (item.product_title || item.title)) || fallback;
  }

  function notifyAdded(title) {
    document.dispatchEvent(new CustomEvent('speakeasy:cart-drawer:refresh'));
    document.dispatchEvent(new CustomEvent('speakeasy:cart-drawer:open'));
    toast('Added to bag — ' + title);
  }

  function cardQuickAdd(button) {
    if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;
    button.setAttribute('aria-busy', 'true');
    var fallbackTitle = button.getAttribute('data-product-title') || 'item';
    postAdd(variantId)
      .then(function (payload) {
        button.removeAttribute('aria-busy');
        notifyAdded(addedTitle(payload, fallbackTitle));
      })
      .catch(function (error) {
        button.removeAttribute('aria-busy');
        toast((error && error.description) || strings.addError);
      });
  }

  /* --------------------------------- modal shell --------------------------------- */

  function openModal(trigger) {
    openedBy = trigger;
    root.hidden = false;
    root.classList.remove('active');
    void root.offsetWidth; /* reflow so the pop-in animation restarts every open */
    root.classList.add('active');
    document.body.classList.add('overflow-hidden');
    if (typeof window.trapFocus === 'function') {
      try {
        window.trapFocus(dialog, dialog);
      } catch (error) {
        dialog.focus();
      }
    } else {
      dialog.focus();
    }
    load(trigger.getAttribute('data-product-url') || '');
  }

  function closeModal() {
    if (!root || root.hidden) return;
    state.requestToken += 1; /* invalidate any in-flight fetch */
    root.classList.remove('active');
    root.hidden = true;
    document.body.classList.remove('overflow-hidden');
    var focusTarget = openedBy;
    openedBy = null;
    if (typeof window.removeTrapFocus === 'function') {
      try {
        window.removeTrapFocus(focusTarget);
      } catch (error) {
        /* focus restore is best-effort */
      }
    } else if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus();
    }
    /* Keep Dawn listeners (media galleries, deferred renders) in sync. */
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
  }

  function showSkeleton() {
    els.skeleton.hidden = false;
    els.error.hidden = true;
    els.content.hidden = true;
    els.title.textContent = 'Loading product';
    dialog.setAttribute('aria-busy', 'true');
  }

  function showError() {
    els.skeleton.hidden = true;
    els.error.hidden = false;
    els.content.hidden = true;
    els.title.textContent = 'Quick view';
    dialog.removeAttribute('aria-busy');
  }

  function showContent() {
    els.skeleton.hidden = true;
    els.error.hidden = true;
    els.content.hidden = false;
    dialog.removeAttribute('aria-busy');
  }

  /* ------------------------------- fetch + render -------------------------------- */

  function load(url) {
    state.lastUrl = url;
    var token = ++state.requestToken;
    showSkeleton();
    var jsonUrl = productJsonUrl(url);
    if (!jsonUrl) {
      showError();
      return;
    }
    fetch(jsonUrl, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Quick view fetch failed: ' + response.status);
        return response.json();
      })
      .then(function (product) {
        if (token !== state.requestToken || root.hidden) return;
        try {
          render(product, url);
        } catch (error) {
          showError();
        }
      })
      .catch(function () {
        if (token !== state.requestToken || root.hidden) return;
        showError();
      });
  }

  function render(product, sourceUrl) {
    if (!product || !product.variants || !product.variants.length) {
      throw new Error('Malformed product payload');
    }
    state.product = product;
    state.options = normalizeOptions(product);

    var initial = null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) {
        initial = product.variants[i];
        break;
      }
    }
    if (!initial) initial = product.variants[0];
    state.selected = state.options.map(function (option, index) {
      return variantOptionValue(initial, index);
    });

    els.title.textContent = product.title || 'Product';

    if (product.vendor) {
      els.maker.textContent = 'by ' + product.vendor;
      els.maker.hidden = false;
    } else {
      els.maker.hidden = true;
    }

    var description = stripHtml(product.description, 300);
    if (description) {
      els.desc.textContent = description;
      els.desc.hidden = false;
    } else {
      els.desc.hidden = true;
    }

    var detailsUrl =
      (typeof product.url === 'string' && product.url) ||
      String(sourceUrl).split('#')[0].split('?')[0];
    state.detailsUrl = detailsUrl.split('?')[0];
    els.details.href = state.detailsUrl;

    setImage(
      initial.featured_image ||
        product.featured_image ||
        (product.images && product.images[0]) ||
        ''
    );
    buildOptionChips();
    updateVariantUI();
    showContent();
  }

  function setImage(image) {
    var src = imageSrcOf(image);
    if (!src) {
      els.img.hidden = true;
      els.img.removeAttribute('src');
      els.img.removeAttribute('srcset');
      return;
    }
    els.img.hidden = false;
    els.img.src = imageWidthUrl(src, 720);
    els.img.srcset = IMAGE_WIDTHS.map(function (width) {
      return imageWidthUrl(src, width) + ' ' + width + 'w';
    }).join(', ');
    els.img.sizes = '(min-width: 750px) 400px, 92vw';
    els.img.alt = (state.product && state.product.title) || '';
  }

  /* ------------------------------- variant chips --------------------------------- */

  function buildOptionChips() {
    var wrap = els.options;
    wrap.innerHTML = '';
    var multi = state.product.variants.length > 1;
    wrap.hidden = !multi;
    if (!multi) return;

    state.options.forEach(function (option, index) {
      var fieldset = document.createElement('fieldset');
      fieldset.className = 'spk-qv__option';

      var legend = document.createElement('legend');
      legend.className = 'spk-qv__option-name';
      legend.textContent = option.name;
      fieldset.appendChild(legend);

      var row = document.createElement('div');
      row.className = 'spk-qv__chips';

      option.values.forEach(function (value) {
        var label = document.createElement('label');
        label.className = 'spk-chip spk-qv__chip';

        var input = document.createElement('input');
        input.type = 'radio';
        input.className = 'spk-qv__chip-input';
        input.name = 'spk-qv-option-' + index;
        input.value = value;
        input.checked = state.selected[index] === value;
        input.setAttribute('data-option-index', String(index));

        var text = document.createElement('span');
        text.className = 'spk-qv__chip-text';
        text.textContent = value;

        label.appendChild(input);
        label.appendChild(text);
        row.appendChild(label);
      });

      fieldset.appendChild(row);
      wrap.appendChild(fieldset);
    });
    syncChips();
  }

  function syncChips() {
    var inputs = els.options.querySelectorAll('.spk-qv__chip-input');
    for (var i = 0; i < inputs.length; i++) {
      var chip = inputs[i].closest('.spk-chip');
      if (chip) chip.classList.toggle('spk-chip--active', inputs[i].checked);
    }
  }

  function resolveVariant() {
    var variants = state.product.variants;
    for (var i = 0; i < variants.length; i++) {
      var match = true;
      for (var j = 0; j < state.selected.length; j++) {
        if (String(variantOptionValue(variants[i], j)) !== String(state.selected[j])) {
          match = false;
          break;
        }
      }
      if (match) return variants[i];
    }
    return null;
  }

  function updateVariantUI() {
    var variant = resolveVariant();
    state.variant = variant;
    els.price.classList.remove('spk-qv__price--sold');

    if (!variant) {
      /* No variant exists for this option combination. */
      els.add.disabled = true;
      els.add.textContent = strings.unavailable;
      els.priceCompare.hidden = true;
      els.details.href = state.detailsUrl;
      return;
    }

    els.priceCurrent.textContent = formatMoney(variant.price);
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      els.priceCompareValue.textContent = formatMoney(variant.compare_at_price);
      els.priceCompare.hidden = false;
    } else {
      els.priceCompare.hidden = true;
    }

    if (variant.featured_image) setImage(variant.featured_image);

    els.details.href =
      state.product.variants.length > 1
        ? state.detailsUrl + '?variant=' + variant.id
        : state.detailsUrl;

    if (variant.available) {
      els.add.disabled = false;
      els.add.textContent = strings.add;
    } else {
      els.add.disabled = true;
      els.add.textContent = strings.soldOut;
      els.price.classList.add('spk-qv__price--sold');
    }
  }

  /* ---------------------------------- handlers ----------------------------------- */

  function onAddClick() {
    if (state.adding || !state.variant || !state.variant.available) return;
    state.adding = true;
    var restoreLabel = els.add.textContent;
    els.add.disabled = true;
    els.add.setAttribute('aria-busy', 'true');
    els.add.textContent = strings.adding;
    var productTitle = (state.product && state.product.title) || 'item';

    postAdd(state.variant.id)
      .then(function (payload) {
        state.adding = false;
        els.add.removeAttribute('aria-busy');
        els.add.disabled = false;
        els.add.textContent = restoreLabel;
        closeModal();
        notifyAdded(addedTitle(payload, productTitle));
      })
      .catch(function (error) {
        state.adding = false;
        els.add.removeAttribute('aria-busy');
        els.add.disabled = false;
        els.add.textContent = restoreLabel;
        toast((error && error.description) || strings.addError);
      });
  }

  function onOptionsChange(event) {
    var input = event.target;
    if (!input || input.type !== 'radio' || input.className.indexOf('spk-qv__chip-input') === -1) {
      return;
    }
    var index = Number(input.getAttribute('data-option-index'));
    if (isNaN(index)) return;
    state.selected[index] = input.value;
    syncChips();
    updateVariantUI();
  }

  function onKeyup(event) {
    var code = String(event.code || event.key || '').toUpperCase();
    if (code === 'ESCAPE') closeModal();
  }

  function onDocumentClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    if (root && !root.hidden) {
      var closer = target.closest('[data-spk-qv-close]');
      if (closer && root.contains(closer)) {
        event.preventDefault();
        closeModal();
        return;
      }
    }

    var trigger = target.closest(TRIGGER_SELECTOR);
    if (trigger) {
      if (!root || !dialog) return; /* no shell: let anchor triggers fall through */
      event.preventDefault();
      openModal(trigger);
      return;
    }

    var cardAdd = target.closest(CARD_ADD_SELECTOR);
    if (cardAdd) {
      event.preventDefault();
      cardQuickAdd(cardAdd);
    }
  }

  /* ------------------------------------ init ------------------------------------- */

  document.addEventListener('click', onDocumentClick);

  if (root && dialog && els) {
    root.addEventListener('keyup', onKeyup);
    if (els.options) els.options.addEventListener('change', onOptionsChange);
    if (els.add) els.add.addEventListener('click', onAddClick);
    if (els.retry) {
      els.retry.addEventListener('click', function () {
        if (state.lastUrl) load(state.lastUrl);
      });
    }
  }
})();
